import React, { FC, useEffect, useState } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { useLocation } from 'react-router'
import { captureException } from '@sentry/browser'
import produce from 'immer'

import { loadMore } from '@sketch/components'
import {
  NotificationCreatedSubscription,
  NotificationCreatedDocument,
  useGetNotificationsQuery,
  GetNotificationsDocument,
  useNotificationMarkAllAsSeenMutation,
  useNotificationMarkAsReadMutation,
  GetUserNotificationsCountDocument,
} from '@sketch/gql-types'
import { getNotificationsQueryOptions } from '@sketch/user'
import { setHasUnreadNotificationsToZero } from './utils'
import { ErrorHandler } from '@sketch/tracing'
import NotificationsErrorBoundary, {
  NotificationsErrorDisplay,
} from './NotificationsErrorBoundary'
import { ParsedError } from '@sketch/graphql-apollo'
import NotificationsListUnread from './NotificationsListUnread'
import NotificationsListRead from './NotificationsListRead'
import { routes } from '@sketch/modules-common'

type NotificationState = 'READ' | 'UNREAD' | 'NEW'

type NotificationsListByTypeProps = {
  notificationType?: NotificationState
}

const convertToUTC = (dateString: string): string => {
  const date = new Date(dateString)

  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  ).toISOString()
}

const updateDate = (type: string, s: string) => {
  const from = s.split(`${type}:`)[1].split(' ')[0]
  const newFrom = convertToUTC(from)
  return s.replace(from, newFrom)
}

/**
 * COMPONENT
 */
const NotificationsListByType: FC<NotificationsListByTypeProps> = () => {
  const client = useApolloClient()
  const [error, setError] = useState<ParsedError | null>(null)
  const [search, setSearch] = useState('')
  const { pathname } = useLocation()

  const isUnreadNotificationsView =
    pathname === routes.UPDATES_UNREAD.template()

  const {
    data,
    fetchMore,
    subscribeToMore,
    loading,
    error: networkError,
  } = useGetNotificationsQuery({
    ...getNotificationsQueryOptions,
    variables: { filter: { search } },
  })

  if (networkError) {
    // This error will be rendered on "NotificationsErrorBoundary"
    throw networkError
  }

  const refetchNotifications = () => {
    client.query({
      query: GetNotificationsDocument,
      ...getNotificationsQueryOptions,
      variables: { filter: { search } },
    })
  }

  const notificationsEntriesPath = ['me', 'notifications', 'entries']
  const notificationsEntries = data?.me.notifications?.entries
  const meta = data?.me.notifications?.meta
  const userId = data?.me?.identifier

  // Mutations
  const [markAllNotificationAsSeen] = useNotificationMarkAllAsSeenMutation({
    redirectErrors: true,
    UNSAFE_ignoreResults: true,
    update: () => userId && setHasUnreadNotificationsToZero(client, userId),
    onError: setError,
    awaitRefetchQueries: true,
    refetchQueries: [{ query: GetUserNotificationsCountDocument }],
  })

  const [markNotificationAsRead] = useNotificationMarkAsReadMutation({
    redirectErrors: true,
    onError: setError,
    refetchQueries: [{ query: GetUserNotificationsCountDocument }],
  })

  useEffect(() => {
    return () => {
      // Ensure up-to-date data by refetching notifications when leaving
      // Updates
      refetchNotifications()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // refetch when search changes
  useEffect(() => {
    refetchNotifications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  const handleSearchUpdate = (newSearch: string) => {
    // converts any dates to UTC
    if (newSearch.includes('from:')) {
      newSearch = updateDate('from', newSearch)
    }

    if (newSearch.includes('to:')) {
      newSearch = updateDate('to', newSearch)
    }

    setSearch(newSearch)
  }

  if (error) {
    // TODO: review the error display UI https://github.com/sketch-hq/Cloud/issues/9314
    return <NotificationsErrorDisplay />
  }

  if (isUnreadNotificationsView) {
    return (
      <NotificationsListUnread
        loading={loading}
        entries={notificationsEntries}
        markAllNotificationAsSeen={markAllNotificationAsSeen}
        markNotificationAsRead={notificationId => {
          markNotificationAsRead({
            variables: { id: notificationId },
            optimisticResponse: {
              __typename: 'RootMutationType',
              notificationMarkAsRead: {
                __typename: 'NotificationMarkAsReadPayload',
                successful: true,
                notification: {
                  __typename: 'CommentNotification',
                  identifier: notificationId,
                  isRead: true,
                },
                errors: [],
              },
            },
          })
        }}
        loadMore={loadMore(fetchMore, meta?.after!, notificationsEntriesPath)}
        subscribeToMore={() =>
          subscribeToMore<NotificationCreatedSubscription>({
            document: NotificationCreatedDocument,
            updateQuery: (prev, { subscriptionData }) => {
              if (!subscriptionData) return prev

              const newNotification = subscriptionData.data.notificationCreated
              if (!newNotification) {
                ErrorHandler.shouldNeverHappen(
                  'There should be always a notification on notificationCreated payload'
                )
                return prev
              }

              return produce(prev, draftState => {
                draftState.me.notifications?.entries.unshift(newNotification)
              })
            },
            onError: captureException,
          })
        }
        onSearchSubmit={handleSearchUpdate}
      />
    )
  }

  return (
    <NotificationsListRead
      loading={loading}
      entries={notificationsEntries}
      loadMore={loadMore(fetchMore, meta?.after!, notificationsEntriesPath)}
      subscribeToMore={() =>
        subscribeToMore<NotificationCreatedSubscription>({
          document: NotificationCreatedDocument,
          updateQuery: (prev, { subscriptionData }) => {
            if (!subscriptionData) return prev

            const newNotification = subscriptionData.data.notificationCreated
            if (!newNotification) {
              ErrorHandler.shouldNeverHappen(
                'There should be always a notification on notificationCreated payload'
              )
              return prev
            }

            return produce(prev, draftState => {
              draftState.me.notifications?.entries.unshift(newNotification)
            })
          },
          onError: captureException,
        })
      }
      hasUnreadNotifications={data?.me?.hasUnreadNotifications || false}
      onSearchSubmit={handleSearchUpdate}
    />
  )
}

const NotificationsListWithDataGuard = (
  props: NotificationsListByTypeProps
) => {
  // TODO: review the error display UI https://github.com/sketch-hq/Cloud/issues/9314
  return (
    <NotificationsErrorBoundary>
      <NotificationsListByType {...props} />
    </NotificationsErrorBoundary>
  )
}

export default NotificationsListWithDataGuard
